/**
 * cypAdhdStructuredRiskConfig.js
 *
 * TOC's NEST-parity structured Risk Assessment for the CYP Clinical
 * Interview (cypAdhdFlow.riskAssessment === 'structuredV1'). Content is
 * VERBATIM from the specification Jasmine fitted to the adult template
 * (received 23 Sept 2026) and is pending Tom's clinical review — change the
 * words only against an updated specification, never ad hoc.
 *
 * Structure: six sections of Yes/No categories, each with the specification's
 * multi-select detail options (and nothing else: no "Other" and no
 * per-category free text, which the specification does not have), followed
 * by one free-text clinical notes field, as on the adult ADHD risk assessment
 * (the "actions taken / escalation" box was removed, Sai, 7 Oct: actions and
 * escalation are recorded off-platform through local risk procedures).
 *
 * The "For Consideration" notice appears while any RISK-INDICATOR category
 * is Yes and requires clinician acknowledgement. Protective factors are
 * deliberately excluded from the trigger (Eve, 23 Sept): a Yes there is good
 * news, not a risk signal — isProtectiveFactors below is what the panel
 * keys that exclusion on.
 *
 * Report rendering: the recorded answers feed the Risk Assessment paragraph
 * of the Clinical Interview report through the LLM drafting pipeline
 * (python-functions/single_report/llm_processor_cyp_ci.py reads
 * structuredRiskAssessment through its own builder and prompt, Oct 2026).
 * The section and category ids and labels below are mirrored in
 * python-functions/constants/cyp_adhd/structured_risk.py, and a backend test
 * fails on any drift between the two, so change them together. Nothing in
 * this module may compute a clinical conclusion (regulatory boundary).
 */

export const CYP_STRUCTURED_RISK_TOOLTIP =
  "This assessment records current and recent risks, relevant incidents, "
  + "vulnerabilities and protective factors. It is a snapshot in time and "
  + "should be reviewed regularly. Risk should be considered in the context "
  + "of the child or young person's age, developmental stage, and protective "
  + "factors. A Yes response for any risk indicators indicates that further "
  + "clinical consideration "
  + "may be required. The clinician remains responsible for assessing the "
  + "significance of the information, taking appropriate action and "
  + "documenting this in accordance with local policy and professional "
  + "responsibilities.";

export const CYP_STRUCTURED_RISK_NOTICE = Object.freeze({
  title: 'For Consideration',
  selectedPrefix: 'You have selected "Yes" to the following risk indicators:',
  guidance:
    'If you have identified risk indicators, please consider whether '
    + 'further assessment, safeguarding action, '
    + 'escalation and/or a risk management plan is required in accordance '
    + 'with local policy and professional responsibilities.',
  platformNote:
    'Risk management functionality is not currently supported by the '
    + 'ThinkDivergent platform.',
  acknowledgementLabel:
    'I acknowledge this notice and will take appropriate clinical action in '
    + 'accordance with local policy and professional responsibilities.',
});

const section = (id, title, categories, extra = {}) =>
  Object.freeze({ id, title, categories: Object.freeze(categories), ...extra });

const category = (id, label, options) =>
  Object.freeze({ id, label, options: Object.freeze(options) });

export const CYP_STRUCTURED_RISK_SECTIONS = Object.freeze([
  section('safetyRisks', 'Current and recent safety risks', [
    category('selfHarmIdeation', 'Self-harm ideation', [
      'In the last 6 months', 'Passive', 'Active with Plan but no Intent',
      'Active with Intent but no Means', 'Active with Means and Intent',
      'Active with Means and no Intent',
    ]),
    category('selfHarm', 'Self-harm / self-injury', [
      'In the last 6 months', 'Current', 'Recent',
      'Increasing frequency or severity', 'NSSI (Non-Suicidal Self-Injury)',
      'Self-poisoning',
    ]),
    category('suicidalIdeation', 'Suicidal thoughts or ideation', [
      'In the last 6 months', 'Passive', 'Active with Plan but no Intent',
      'Active with Intent but no Means', 'Active with Means and Intent',
      'Active with Means and no Intent',
    ]),
    category('suicideAttempts', 'Suicide attempt(s)', [
      'In the last 6 months', 'Current or recent concern', 'Historical',
      'Single', 'Multiple',
    ]),
    category('risksToOthers', 'Risks to others', [
      'Threats', 'Physical aggression', 'Violence', 'Harm to parents or carers',
      'Harm to siblings', 'Harm to peers', 'Harm to school staff or clinicians',
      'Use of weapons', 'Dangerous objects',
    ]),
    category('cumulativeRisk', 'Cumulative or escalating risk', [
      'Increasing frequency', 'Increasing severity', 'Pattern of incidents',
      'Aggressive behaviour contributing to cumulative risk',
    ]),
  ]),

  section('safeguarding', 'Safeguarding and vulnerability', [
    category('abuse', 'Abuse', [
      'Physical', 'Emotional', 'Sexual', 'Discriminatory', 'Online',
      'Psychological', 'Financial or material', 'Organisational or institutional',
      'Coercive control', 'Suspected', 'Disclosed', 'Current', 'Historical',
    ]),
    category('neglect', 'Neglect', [
      'Suspected', 'Confirmed', 'Current', 'Historical',
    ]),
    category('domesticAbuse', 'Domestic abuse / violence', [
      'Current', 'Historical but remains living with alleged perpetrator',
      'Historical',
    ]),
    category('goingMissing', 'Going missing / absconding', [
      'Current concern', 'Previous episodes', 'Increasing frequency',
      'High vulnerability while missing', 'None',
    ]),
    category('bullying', 'Bullying / cyberbullying', [
      'In-person', 'Online', 'Peer group', 'School', 'Community',
      'Current', 'Historical',
    ]),
    category('exploitation', 'Exploitation', [
      'Child sexual exploitation', 'Child criminal exploitation', 'Grooming',
      'County lines', 'Gang-related', 'Trafficking', 'Modern slavery',
      'Financial exploitation', 'Cuckooing',
    ]),
    category('harmfulSexualBehaviour', 'Harmful sexual behaviour (HSB)', [
      'Current', 'Recent', 'Suspected', 'Disclosed', 'Historical',
      'Directed towards self', 'Directed towards others', 'Coercion',
    ]),
    category('fgm', 'Female Genital Mutilation (FGM)', [
      'Concern', 'Disclosure', 'Known history', 'Risk of FGM',
    ]),
    category('socialCare', 'Social care involvement', [
      'Child in Need (CIN)', 'Child Looked After (CLA)', 'Child Protection',
      'Parental/carer mental health concerns',
    ]),
    category('housing', 'Housing / accommodation concern', [
      'Homelessness', 'Risk of homelessness', 'Housing instability',
      'Unsafe accommodation', 'Temporary accommodation',
    ]),
    category('radicalisation', 'Radicalisation', [
      'Exposure to extremist material', 'Vulnerability to radicalisation',
      'Prevent referral indicated',
    ]),
  ]),

  section('contextualConcerns', 'Other incident and contextual concerns', [
    category('policeInvolvement', 'Police involvement', [
      'Within the last 6 months', 'Current', 'Historical', 'In relation to CYP',
      'In relation to home or household',
    ]),
    category('schoolAbsence', 'School absence', [
      'Current', 'Persistent', 'Increasing',
    ]),
    category('repeatedRequestsForHelp', 'Persistent or repeated requests for help', [
      'Repeated contact', 'Increasing frequency of contact',
      'Concerns despite appearing currently safe',
    ]),
    category('gpRegistration', 'Not registered with a GP', [
      'Not registered with a GP', 'Registration uncertain',
    ]),
  ]),

  section('substanceUse', 'Substance use and medication safety', [
    category('drugUse', 'Drug use / misuse', [
      'Cannabis', 'Stimulants', 'Ketamine', 'Opioids', 'Solvents or inhalants', 'Current', 'Recent', 'Increasing', 'None',
    ]),
    category('alcoholUse', 'Alcohol use / misuse', [
      'Current', 'Recent', 'Increasing', 'Risk-taking associated with use', 'None',
    ]),
    category('medicationConcerns', 'Medication-related concerns', [
      'Unsafe storage', 'Unsupervised access', 'Medication diversion',
      'Illegal purchasing of prescribed medication',
      'Concerns about consent to medication',
      'Deliberate misuse (e.g., weight control)',
    ]),
  ]),

  section('onlineSafety', 'Online safety and digital risk', [
    category('internetSafety', 'Internet safety concerns', [
      'Cyberbullying', 'Online grooming', 'Sexual exploitation',
      'Contact with unsafe individuals', 'Sharing risky content',
      'Exposure to harmful content', 'Exposure to extremist content',
      'Excessive or problematic use', 'Risky social media',
      'Anonymous platforms', 'Significant impact on sleep or functioning',
    ]),
  ]),

  // Protective factors NEVER trigger the For Consideration notice — a Yes
  // here is a strength, not a risk indicator (Eve, 23 Sept).
  section('protectiveFactors', 'Protective factors', [
    category('protectiveFactors', 'Protective factors', [
      'Supportive parent/carer', 'Trusted adult', 'Strong family support',
      'Stable home environment', 'Cultural/community support',
      'Positive peer relationships', 'Engagement with school/education',
      'Future goals', 'Sense of belonging', 'Safe online connections',
      'Insight', 'Healthy coping strategies',
    ]),
  ], { isProtectiveFactors: true }),
]);

/** Flat category lookup: id → { label, sectionId, isProtectiveFactors }. */
export const CYP_STRUCTURED_RISK_CATEGORY_INDEX = Object.freeze(
  CYP_STRUCTURED_RISK_SECTIONS.reduce((acc, s) => {
    s.categories.forEach((c) => {
      acc[c.id] = Object.freeze({
        label: c.label,
        sectionId: s.id,
        isProtectiveFactors: Boolean(s.isProtectiveFactors),
      });
    });
    return acc;
  }, {}),
);

/**
 * The labels of every risk-indicator category currently answered Yes —
 * exactly the list the For Consideration notice shows. Protective factors
 * are excluded by design.
 */
export function listYesRiskIndicators(categoriesData = {}) {
  return CYP_STRUCTURED_RISK_SECTIONS.flatMap((s) =>
    s.isProtectiveFactors
      ? []
      : s.categories
          .filter((c) => categoriesData[c.id]?.present === true)
          .map((c) => c.label),
  );
}

/**
 * True when the stored acknowledgement covers every risk indicator currently
 * answered Yes. An indicator added after acknowledging has not been
 * acknowledged, so the clinician must acknowledge again.
 */
export function isAcknowledgementCurrent(acknowledgement, yesRiskIndicators) {
  if (!acknowledgement?.acknowledged) return false;
  const acknowledged = acknowledgement.riskIndicators || [];
  return yesRiskIndicators.every((label) => acknowledged.includes(label));
}
